import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Merge, ArrowRight } from "lucide-react";
import { formatFechaLarga } from "@/lib/agenda";

export default function UnificarDialog({ open, onOpenChange, fecha, items = [], onDone }) {
  const [tourId, setTourId] = useState("");
  const [origen, setOrigen] = useState([]);
  const [destino, setDestino] = useState("");
  const [saving, setSaving] = useState(false);

  // Tours con más de un horario en el día (candidatos a unificar)
  const porTour = useMemo(() => {
    const map = {};
    for (const i of items) {
      if (!map[i.tour_id]) map[i.tour_id] = { tour_id: i.tour_id, nombre: i.tour_nombre, horas: {} };
      const h = map[i.tour_id].horas;
      h[i.hora] = (h[i.hora] || 0) + i.reservas_count;
    }
    return Object.values(map)
      .map((t) => ({ ...t, horasArr: Object.entries(t.horas).sort() }))
      .filter((t) => t.horasArr.length > 1);
  }, [items]);

  useEffect(() => {
    if (open) {
      setTourId("");
      setOrigen([]);
      setDestino("");
    }
  }, [open]);

  const tourSel = porTour.find((t) => t.tour_id === tourId);
  const horas = tourSel?.horasArr || [];
  const origenValido = origen.filter((h) => h !== destino);
  const totalReservas = origenValido.reduce((acc, h) => {
    const found = horas.find(([hh]) => hh === h);
    return acc + (found ? found[1] : 0);
  }, 0);

  const toggleOrigen = (hora) => {
    setOrigen((prev) => (prev.includes(hora) ? prev.filter((h) => h !== hora) : [...prev, hora]));
  };

  const confirmar = async () => {
    const horasOrigen = origen.filter((h) => h !== destino);
    if (!tourId) return toast.error("Elegí un tour.");
    if (!destino) return toast.error("Elegí el horario destino.");
    if (horasOrigen.length === 0) return toast.error("Elegí al menos un horario de origen distinto al destino.");
    setSaving(true);
    try {
      const { data } = await api.post("/agenda/unificar", {
        tour_id: tourId,
        fecha,
        horas_origen: horasOrigen,
        hora_destino: destino,
      });
      toast.success(`${data.movidas} reserva(s) movidas al horario ${destino}.`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="unificar-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Merge className="h-5 w-5" /> Unificar horarios
          </DialogTitle>
          <DialogDescription>
            Cuando un tour tiene varias salidas que no se llenan, movelas a un único horario. Las reservas se
            reagrupan y se recalcula el cupo.
          </DialogDescription>
        </DialogHeader>

        {porTour.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No hay tours con más de un horario en este día.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Tour</Label>
              <Select
                value={tourId}
                onValueChange={(v) => {
                  setTourId(v);
                  setOrigen([]);
                  setDestino("");
                }}
              >
                <SelectTrigger data-testid="unificar-tour-select">
                  <SelectValue placeholder="Elegí un tour con varios horarios" />
                </SelectTrigger>
                <SelectContent>
                  {porTour.map((t) => (
                    <SelectItem key={t.tour_id} value={t.tour_id}>
                      {t.nombre} ({t.horasArr.length} horarios)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {tourSel && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Horarios a mover (origen)</Label>
                  <div className="space-y-1.5 border border-slate-200 rounded-md p-2">
                    {horas.map(([hora, count]) => (
                      <label
                        key={hora}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                        data-testid={`unificar-origen-${hora}`}
                      >
                        <Checkbox
                          checked={origen.includes(hora)}
                          onCheckedChange={() => toggleOrigen(hora)}
                          disabled={hora === destino}
                        />
                        <span className="font-mono tabular-nums">{hora}</span>
                        <span className="text-slate-400 text-xs">· {count} reserva(s)</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Horario destino</Label>
                  <Select value={destino} onValueChange={setDestino}>
                    <SelectTrigger data-testid="unificar-destino-select">
                      <SelectValue placeholder="Horario al que se mueven" />
                    </SelectTrigger>
                    <SelectContent>
                      {horas.map(([hora]) => (
                        <SelectItem key={hora} value={hora}>
                          {hora}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {origenValido.length > 0 && destino && (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                    data-testid="unificar-resumen"
                  >
                    <p className="font-medium flex items-center gap-1.5 flex-wrap">
                      Vas a mover <span className="font-bold">{totalReservas} reserva(s)</span> de las
                      <span className="inline-flex items-center gap-1 font-mono">
                        {origenValido.join(", ")}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5" />
                      <span className="font-mono font-bold">{destino}</span>
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Tour <strong>{tourSel?.nombre}</strong> · {formatFechaLarga(fecha)}. Solo se mueven las
                      horas que marcaste arriba; el resto de los horarios del día queda intacto.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={confirmar}
            disabled={saving || porTour.length === 0 || origenValido.length === 0 || !destino}
            data-testid="unificar-confirm"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : totalReservas > 0 ? (
              `Mover ${totalReservas} reserva(s) a las ${destino}`
            ) : (
              "Unificar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
